import React, { useEffect, useState } from "react"
import { css, Theme } from "@emotion/react"
import {
	CustomerPartQueryParams,
	initialCustomerPartQueryParams,
	OnlinePartEcomm,
	useCustomerPartsEcomm,
	useSearchOnlinePartsEcomm,
} from "@ncs/ncs-api"
import { arrayWrap } from "@ncs/ts-utils"
import {
	Box,
	Button,
	GridContainer,
	GridItem,
	LoadingSpinner,
	VisualSearchCameraCircleIcon,
	Paragraph,
	ResultCountSummaryEcomm,
	useUrlState,
	handleVisualSearch,
	TrackingEvent,
	toggleTrailingSlash,
} from "@ncs/web-legos"
import { Flag, useIsFeatureFlagOn } from "@ncs/web-legos"
import {
	AddedToCartModalEcomm,
	PageContentWrapper,
	PartPreviewRowEcomm,
} from "~/shared-components"
import {
	PaginationEcomm,
	PartSearchSidebarEcomm,
	PartCard,
	PartSortEcomm,
	ActiveFiltersEcomm,
} from "./components"
import { SearchFilterModalEcomm } from "./components/Filters/SearchFilterModalEcomm"
import { useAnalytics } from "~/hooks/useAnalytics"
import { useLocation } from "react-router-dom"

export type PartSearchResultsUrlState = Omit<
	CustomerPartQueryParams,
	"categories" | "manufacturers" | "systems" | "chemicals"
> & {
	// We're changing these types so that the backend doesn't need to JSON stringify these.
	manufacturers: string | number | (string | number)[]
	categories: string | number | (string | number)[]
	systems: string | number | (string | number)[]
	chemicals: string | number | (string | number)[]
	brands: string | number | (string | number)[]
	priceRanges?: string | string[]
	page?: number
}

export const PartSearchResults: React.FC = () => {
	const [addedPart, setAddedPart] = useState<OnlinePartEcomm | null>(null)
	const [params] = useUrlState<PartSearchResultsUrlState>({
		...initialCustomerPartQueryParams,
		page: undefined,
	})
	const { trackEvent } = useAnalytics()
	const { pathname } = useLocation()

	const hideTrending = useIsFeatureFlagOn(Flag.VITE_FLAG_HIDE_TRENDING)

	const {
		data: parts,
		currentPage,
		resultPageCount,
		isLoading,
		resultCountEstimate,
		fetchPage,
		match,
		originalTerm,
		correctedTerms,
	} = useSearchOnlinePartsEcomm({
		params: {
			...params,
			categories: arrayWrap(params.categories).map((c) => String(c)),
			manufacturers: arrayWrap(params.manufacturers).map((m) => String(m)),
			systems: arrayWrap(params.systems).map((s) => String(s)),
			chemicals: arrayWrap(params.chemicals).map((s) => String(s)),
			show_children: false,
		},
		pageSize: 24,
		removeChildren: true,
	})

	const [showSearchFilterModal, setShowSearchFilterModal] = useState(false)

	const handleVisualSearchOnSearchResults = () => {
		handleVisualSearch()
		trackEvent(TrackingEvent.VISUAL_SEARCH_ICON)
	}

	const handleAddToCart = (selectedPart: OnlinePartEcomm) => {
		trackEvent(TrackingEvent.SR_ADD_TO_CART, {
			id: selectedPart.id,
			item_value: selectedPart.price,
			quantity: 1,
			name: selectedPart.part?.description,
			part_title_name: selectedPart.title,
			part_number: selectedPart.onlinePartNumber,
		})
		setAddedPart(selectedPart)
	}

	const buildPageUrl = (page: number): string => {
		const updatedParams = { ...params, page }
		const searchParams = new URLSearchParams()

		Object.entries(updatedParams).forEach(([key, value]) => {
			if (value !== null && value !== undefined && value !== "") {
				if (Array.isArray(value) && value.length > 0) {
					searchParams.set(key, JSON.stringify(value))
				} else if (!Array.isArray(value)) {
					searchParams.set(key, JSON.stringify(value))
				}
			}
		})

		return `${toggleTrailingSlash(pathname)}?${searchParams.toString()}`
	}

	useEffect(() => {
		if (params.page && params.page !== currentPage) {
			fetchPage(params.page)
		} else if (!params.page && currentPage !== 1) {
			fetchPage(1)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		params.page,
		params.search,
		params.ordering,
		params.manufacturers,
		params.categories,
		params.systems,
		params.chemicals,
	])

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" })
	}, [
		params.page,
		params.search,
		params.ordering,
		params.manufacturers,
		params.categories,
		params.systems,
		params.chemicals,
	])

	const shouldShowFallbackSections = !isLoading && parts.length === 0 && match === false

	const { data: trendingParts = [], isLoading: isLoadingTrendingParts } = useCustomerPartsEcomm({
		params: {
			featured: true,
			ordering: "title",
		},
		pageSize: 10,
		removeChildren: true,
		queryConfig: {
			enabled: shouldShowFallbackSections && !hideTrending,
		},
	})

	const { data: topSearchParts, isLoading: isLoadingTopSearchParts } = useCustomerPartsEcomm({
		params: {
			ordering: "title",
		},
		pageSize: 10,
		removeChildren: true,
		queryConfig: {
			enabled: shouldShowFallbackSections,
		},
	})

	return (
		<PageContentWrapper
			title="Product Search"
			breadcrumbs={[{ name: "Shop", to: "/shop" }, { name: "Search" }]}
		>
			<div css={contentContainer}>
				<div css={sideBarFilter}>
					<PartSearchSidebarEcomm />
				</div>
				<div css={mainContentArea}>
					<div css={searchResultsStatusHeaderStyle}>
						<div css={resultStyle}>
							<ResultCountSummaryEcomm
								currentTotal={parts.length}
								totalResults={resultCountEstimate}
								searchItem={params.search}
								originalTerm={originalTerm}
								correctedTerm={correctedTerms?.[0]}
								correctedTerms={correctedTerms}
								match={match}
							/>
						</div>

						<div css={filterHeader}>
							<div css={filterButton}>
								<Button onClick={() => setShowSearchFilterModal(true)}>
									Filters
								</Button>
							</div>
							<PartSortEcomm />
						</div>
					</div>

					<ActiveFiltersEcomm />

					<Box mb={2} css={visualSearchIconBoxStyle}>
						<Button onClick={handleVisualSearchOnSearchResults}>
							<VisualSearchCameraCircleIcon />
						</Button>
						<Box css={snapSearchBoxStyle}>
							<p css={snapSearchStyle}>ClickNPic</p>
							<p css={visualSearchDescriptionStyle}>
								Easily find what you need! Simply take or upload a photo, and let
								our AI do the work.
							</p>
						</Box>
					</Box>

					{isLoading && (
						<Box
							width="100%"
							height="3000px"
							display="flex"
							alignItems="flex-start"
							justifyContent="center"
						>
							<LoadingSpinner
								text="Searching..."
								py={10}
								height="20px"
								width="20px"
							/>
						</Box>
					)}

					{shouldShowFallbackSections && (
						<>
							{!isLoadingTopSearchParts && (
								<PartPreviewRowEcomm
									heading="Popular Searches"
									partIds={topSearchParts.map((p: OnlinePartEcomm) => p.id)}
									ordering="title"
								/>
							)}

							{!isLoadingTrendingParts && !hideTrending && (
								<PartPreviewRowEcomm
									heading="Trending Products"
									partIds={trendingParts.map((p: OnlinePartEcomm) => p.id)}
								/>
							)}
						</>
					)}

					<GridContainer>
						{parts.map((part: OnlinePartEcomm) => (
							<GridItem
								key={part.id}
								xs={12}
								sm={6}
								md={4}
								lg={3}
								mb={4}
								css={gridPartCard}
							>
								<PartCard
									part={part}
									onAddToCart={() => handleAddToCart(part)}
									css={partCardStyle}
									widthMode="fluid"
								/>
							</GridItem>
						))}
					</GridContainer>

					{parts.length > 0 && (
						<PaginationEcomm
							numberOfPages={resultPageCount ?? 0}
							buildPageUrl={buildPageUrl}
							currentPage={currentPage ?? 0}
							isLoading={isLoading}
						/>
					)}

					{!isLoading && parts.length === 0 && match !== false && (
						<Box my={8} textAlign="center">
							<Paragraph mt={2} color="secondary">
								Sorry, no results were found. Try a different search?
							</Paragraph>
						</Box>
					)}
				</div>
			</div>

			<AddedToCartModalEcomm
				isOpen={!!addedPart}
				partId={addedPart?.id}
				onClose={() => setAddedPart(null)}
			/>
			<SearchFilterModalEcomm
				isOpen={showSearchFilterModal}
				onClose={() => setShowSearchFilterModal(false)}
			/>
		</PageContentWrapper>
	)
}

const contentContainer = (theme: Theme) => css`
	display: grid;
	grid-template-columns: auto 1fr;
	column-gap: 3rem;

	${theme.breakpoints.down("sm")} {
		grid-template-columns: auto;
		column-gap: 0;
	}
`

const sideBarFilter = (theme: Theme) => css`
	${theme.breakpoints.down("sm")} {
		display: none;
	}
`

const mainContentArea = css`
	min-width: 0;
`

const filterButton = (theme: Theme) => css`
	border-radius: 0.5rem;
	border: 1px solid #374151;
	padding: 0.7rem 1rem;
	font-size: 0.8rem;
	color: #374151;

	> span {
		font-weight: 500;
	}

	${theme.breakpoints.up("md")} {
		display: none;
	}
`

const gridPartCard = css`
	display: grid;
`

const partCardStyle = css`
	height: 100%;
	width: 100%;
`

const resultStyle = (theme: Theme) => css`
	margin-bottom: 0;

	${theme.breakpoints.down("md")} {
		margin-bottom: 2rem;
	}
`

const filterHeader = (theme: Theme) => css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding-right: 1rem;
`

const searchResultsStatusHeaderStyle = (theme: Theme) => css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 1.5rem;

	> div {
		align-self: normal;
	}

	${theme.breakpoints.down("sm")} {
		flex-direction: column;
		align-items: space-between;
	}
`

const snapSearchStyle = css`
	color: #111827;
	font-size: 16px;
	font-style: normal;
	font-weight: 700;
`

const visualSearchDescriptionStyle = css`
	color: #374151;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 20px;
	margin-top: 5px;
`

const visualSearchIconBoxStyle = css`
	display: flex;
	padding: 24px;
	justify-content: start;
	align-items: flex-start;
	gap: 10px;
	border-radius: 8px;
	border: 1px solid #d1d5db;
	background: #f3f4f6;
`

const snapSearchBoxStyle = css`
	margin-left: 12px;
	padding-right: 48px;
`
